import React, { useRef, useState } from 'react';

import SubcontractorIcon from 'icons/ic-sub-contractors-large.svg';
import { useTranslation } from 'react-i18next';

import PanelIcon from 'components/application/PanelIcon';
import SidePanel from 'components/application/SidePanel';

import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';
import SubcontractorsForm from 'components/subcontractors/SubcontractorsForm';
import SubcontractorsShow from 'components/subcontractors/SubcontractorsShow';

import { focusField } from 'components/helpers/refs';

export default function SubcontractorsSidePanel({
  allowShowFooterContents,
  closeSidePanel,
  createSubcontractor,
  currentSubcontractor,
  onDeleteClick,
  onSubcontractorInputChange,
  removeErrorStyling,
  requestError,
  setSidePanelContext,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
  updateSubcontractor,
}) {
  const sidePanelBodyRef = useRef();
  const sidePanelFieldRef = useRef();

  const [scrolledAmount, setScrolledAmount] = useState(0);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'subcontractors.SubcontractorsSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const openEditSubcontractor = () => {
    setSidePanelContext('edit');
  };

  const showBodyContent = (
    <SubcontractorsShow
      currentSubcontractor={currentSubcontractor}
      sidePanelIsOpen={sidePanelIsOpen}
    />
  );

  const formBodyContent = (
    <SubcontractorsForm
      currentSubcontractor={currentSubcontractor}
      onSubcontractorInputChange={onSubcontractorInputChange}
      removeErrorStyling={removeErrorStyling}
      requestError={requestError}
      sidePanelFieldRef={sidePanelFieldRef}
      submitDisabled={submitDisabled}
    />
  );

  const newFooterContent = (
    <FormFooter
      onCancel={closeSidePanel}
      onSubmit={createSubcontractor}
      submitButtonDisabled={submitDisabled}
      text={i18nTCommon('subcontractors.addNewSubContractor')}
    />
  );

  const showFooterContent = allowShowFooterContents && (
    <ShowFooter
      deleteDisabled={currentSubcontractor.isDeleteProhibited}
      deleteDisabledTooltip={i18nT('show.delete.disabledTooltip')}
      deleteText={i18nTCommon('subcontractors.deleteSubContractor')}
      editText={i18nTCommon('subcontractors.editSubContractor')}
      onDeleteClick={onDeleteClick}
      onEditClick={openEditSubcontractor}
    />
  );

  const editFooterContent = (
    <FormFooter
      onCancel={closeSidePanel}
      onSubmit={updateSubcontractor}
      submitButtonDisabled={submitDisabled}
      text={i18nTCommon('shared.saveChanges')}
    />
  );

  const footerContent = () => {
    switch (sidePanelContext) {
      case 'new':
        return newFooterContent;
      case 'show':
        return showFooterContent;
      case 'edit':
        return editFooterContent;
    }
  };

  const onBackdropClick = () => {
    const panelLocked = ['new', 'edit'].includes(sidePanelContext);

    if (!panelLocked) closeSidePanel();
  };

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ? showBodyContent : formBodyContent
      }
      bodyRef={sidePanelBodyRef}
      closeCallback={closeSidePanel}
      color={sidePanelContext === 'show' ? 'secondary' : 'tertiary'}
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footerContent()}
      headerContent={
        sidePanelContext === 'show' ?
          <PanelIcon Icon={SubcontractorIcon} scrolledAmount={scrolledAmount} />
        : <div className='popup__title popup__title--tertiary'>
            <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {sidePanelContext === 'edit' ?
                i18nTCommon('subcontractors.editSubContractor')
              : i18nTCommon('subcontractors.addNewSubContractor')}
            </h1>
          </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onBackdropClick}
      onOpen={() => sidePanelContext === 'new' && focusField(sidePanelFieldRef)}
      scrollHeader={sidePanelContext === 'show'}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}
